import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Play, Pause, Flag, X, Trash2, Share2 } from "lucide-react";
import { buildShareCard, shareCard } from "@/lib/shareCard";
import { PositionPicker } from "@/components/familia/PositionPicker";

export const LIVE_KEY = "familia_live_match";

const load = () => { try { return JSON.parse(localStorage.getItem(LIVE_KEY) || "null"); } catch { return null; } };
const persist = (s) => { try { localStorage.setItem(LIVE_KEY, JSON.stringify(s)); } catch {} };
export const clearLive = () => { try { localStorage.removeItem(LIVE_KEY); } catch {} };

const fmt = (sec) => `${String(Math.floor(sec / 60)).padStart(2, "0")}:${String(Math.floor(sec % 60)).padStart(2, "0")}`;

const EVENT_LABEL = {
  gol: "⚽ Gol", gol_equipo: "⚽ Gol del equipo", gol_rival: "🥅 Gol rival",
  asistencia: "🅰 Asistencia", remate: "🎯 Remate", remate_cabeza: "🎯 Remate de cabeza",
  amarilla: "🟨 Amarilla", roja: "🟥 Roja",
  pase_ok: "✓ Pase bueno", perdida: "✕ Pérdida", recuperacion: "🔄 Recuperación",
  regate: "🏃 Regate", duelo: "⚔ Duelo ganado",
  aereo: "🆙 Juego aéreo", intercepcion: "🛡 Intercepción", entrada: "🦵 Entrada",
  centro: "↪ Centro", pase_clave: "🔑 Pase clave",
  entra: "🔁 Entra", sale: "🔁 Sale", momento: "⭐ Momento",
  descanso: "⏸ Descanso", segunda_parte: "▶ Inicio 2ª parte",
};

const BASIC_BTNS = [["gol", "⚽ Gol"], ["asistencia", "🅰 Asist."], ["remate", "🎯 Remate"], ["remate_cabeza", "🎯 R. cabeza"], ["amarilla", "🟨 Amarilla"], ["roja", "🟥 Roja"]];
const TECH_BTNS = [["pase_ok", "✓ Pase"], ["perdida", "✕ Pérdida"], ["recuperacion", "🔄 Recup."], ["regate", "🏃 Regate"], ["duelo", "⚔ Duelo"], ["aereo", "🆙 Aéreo"], ["intercepcion", "🛡 Interc."], ["entrada", "🦵 Entrada"], ["centro", "↪ Centro"], ["pase_clave", "🔑 P. clave"]];
const TECH_TYPES = TECH_BTNS.map(([t]) => t);
const TECH_FIELD = { pase_ok: "pases_ok", perdida: "perdidas", recuperacion: "recuperaciones", regate: "regates", duelo: "duelos_ganados", aereo: "juego_aereo", intercepcion: "intercepciones", entrada: "entradas", centro: "centros", pase_clave: "pases_clave" };

export default function FamiliaLiveMatch() {
  const navigate = useNavigate();
  const [state, setState] = useState(() => load() || { phase: "setup" });
  const [tick, setTick] = useState(0);
  const [saving, setSaving] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (state.phase === "share") clearLive();
    else persist(state);
  }, [state]);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // ---- setup ----
  const [players, setPlayers] = useState([]);
  const [team, setTeam] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data: teams } = await api.get("/teams");
        const t = teams[0];
        setTeam(t);
        if (t) {
          const { data: pls } = await api.get(`/players?team_id=${t.id}`);
          setPlayers(pls);
        }
      } catch { toast.error("No se pudieron cargar tus jugadores"); }
    })();
  }, []);

  const elapsed = useMemo(() => {
    if (state.phase !== "live") return 0;
    const c = state.clock;
    return Math.floor(c.accSec + (c.running ? (Date.now() - c.lastStart) / 1000 : 0));
  }, [state, tick]); // eslint-disable-line
  const minuto = Math.floor(elapsed / 60) + 1;

  const startMatch = (setup) => {
    let adv = false;
    try { adv = localStorage.getItem("familia_adv_mode") === "1"; } catch {}
    setState({
      phase: "live",
      adv,
      match: setup.match,
      players: setup.players.filter((p) => p.playing).map((p) => ({
        id: p.id, name: p.name, titular: p.titular,
        onField: p.titular, fieldSince: 0, playedSec: 0,
      })),
      clock: { running: false, accSec: 0, lastStart: Date.now(), half: 1 },
      events: [],
    });
  };

  const toggleClock = () => setState((s) => {
    const c = s.clock;
    return { ...s, clock: c.running
      ? { running: false, accSec: Math.floor(c.accSec + (Date.now() - c.lastStart) / 1000), lastStart: c.lastStart }
      : { ...c, running: true, accSec: c.accSec, lastStart: Date.now() } };
  });

  const halfTime = () => setState((s) => {
    const c = s.clock;
    const acc = c.running ? Math.floor(c.accSec + (Date.now() - c.lastStart) / 1000) : c.accSec;
    return { ...s, clock: { ...c, running: false, accSec: acc, half: "ht" },
      events: [...s.events, { id: `${Date.now()}-ht`, sec: acc, min: Math.floor(acc / 60) + 1, type: "descanso" }] };
  });

  const startSecondHalf = () => setState((s) => ({
    ...s, clock: { ...s.clock, running: true, lastStart: Date.now(), half: 2 },
    events: [...s.events, { id: `${Date.now()}-2h`, sec: s.clock.accSec, min: Math.floor(s.clock.accSec / 60) + 1, type: "segunda_parte" }],
  }));

  const addEvent = (type, playerId = null, texto = null) => setState((s) => ({
    ...s,
    events: [...s.events, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, sec: elapsed, min: minuto, type, player_id: playerId, texto }],
  }));

  const toggleField = (pid) => setState((s) => {
    const players2 = s.players.map((p) => {
      if (p.id !== pid) return p;
      if (p.onField) return { ...p, onField: false, playedSec: p.playedSec + (elapsed - p.fieldSince) };
      return { ...p, onField: true, fieldSince: elapsed };
    });
    const p = s.players.find((x) => x.id === pid);
    return { ...s, players: players2,
      events: [...s.events, { id: `${Date.now()}-f`, sec: elapsed, min: minuto, type: p.onField ? "sale" : "entra", player_id: pid }] };
  });

  const delEvent = (id) => setState((s) => ({ ...s, events: s.events.filter((e) => e.id !== id) }));

  const cntFor = (type, playerId) => state.events.filter((e) => e.type === type && e.player_id === playerId).length;

  const playerMinutes = (p) => Math.round((p.playedSec + (p.onField ? elapsed - p.fieldSince : 0)) / 60);

  const summary = useMemo(() => {
    if (state.phase !== "live" && state.phase !== "review") return null;
    const per = {};
    for (const p of state.players) per[p.id] = { name: p.name, titular: p.titular, min: playerMinutes(p), gols: 0, asist: 0, remates: 0, tg: 0, tr: 0, pases: 0, perdidas: 0, recup: 0, regates: 0, duelos: 0, aereos: 0, interc: 0, entradas: 0, centros: 0, claves: 0 };
    let gf = 0, gc = 0;
    for (const e of state.events) {
      if (e.type === "gol") { gf += 1; if (per[e.player_id]) per[e.player_id].gols += 1; }
      if (e.type === "gol_equipo") gf += 1;
      if (e.type === "gol_rival") gc += 1;
      const map = { asistencia: "asist", remate: "remates", remate_cabeza: "remates", amarilla: "tg", roja: "tr", pase_ok: "pases", perdida: "perdidas", recuperacion: "recup", regate: "regates", duelo: "duelos", aereo: "aereos", intercepcion: "interc", entrada: "entradas", centro: "centros", pase_clave: "claves" };
      if (map[e.type] && per[e.player_id]) per[e.player_id][map[e.type]] += 1;
    }
    return { per, gf, gc };
  }, [state, tick]); // eslint-disable-line

  const finish = () => {
    setState((s) => {
      const c = s.clock;
      const acc = c.running ? Math.floor(c.accSec + (Date.now() - c.lastStart) / 1000) : c.accSec;
      const players2 = s.players.map((p) => p.onField ? { ...p, onField: false, playedSec: p.playedSec + (acc - p.fieldSince) } : p);
      return { ...s, phase: "review", players: players2, clock: { ...c, running: false, accSec: acc } };
    });
  };

  const save = async () => {
    const s = stateRef.current;
    if (!team) return toast.error("No se pudo cargar tu equipo — revisa tu conexión y reintenta");
    const gf = s.events.filter((e) => e.type === "gol" || e.type === "gol_equipo").length;
    const gc = s.events.filter((e) => e.type === "gol_rival").length;
    setSaving(true);
    try {
      await api.post("/jornadas", {
        team_id: team.id, session_type: "partido", date: s.match.date,
        opponent: s.match.opponent, home_away: s.match.home_away, status: "final",
        team_stats: { gols_favor: gf, gols_contra: gc, resultado: gf > gc ? "V" : gf === gc ? "E" : "D" },
        player_stats: s.players.map((p) => {
          const cnt = (t) => s.events.filter((e) => e.type === t && e.player_id === p.id).length;
          const adv = s.adv || TECH_TYPES.some((t) => cnt(t) > 0);
          const tech = Object.fromEntries(Object.entries(TECH_FIELD).map(([t, f]) => [f, adv ? cnt(t) : null]));
          return {
            player_id: p.id, convocado: true, titular: p.titular,
            posicion: p.posicion || null,
            minutos: Math.round(p.playedSec / 60),
            gols: cnt("gol"), asistencias: cnt("asistencia"),
            shots_on_goal: cnt("remate") + cnt("remate_cabeza"),
            targetes_grogues: cnt("amarilla"), targetes_roges: cnt("roja"),
            // Técnicos: solo se registran si se usó el modo técnico (si no, quedan "sin registrar")
            ...tech,
          };
        }),
      });
      const moms = s.events.filter((x) => x.type === "momento" && x.texto);
      const results = await Promise.allSettled(moms.map((e) =>
        api.post("/familias/momentos", { player_id: e.player_id || s.players[0].id, date: s.match.date, minuto: e.min, texto: e.texto })
      ));
      const failed = results.filter((r) => r.status === "rejected").length;
      clearLive();
      if (failed) toast.warning(`Partido guardado, pero ${failed} momento${failed > 1 ? "s" : ""} ⭐ no se pudieron guardar`);
      else toast.success("Partido guardado ⚽");
      // Fase compartir: tarjeta-resumen por hijo lista para WhatsApp
      const cards = s.players.filter((p) => Math.round(p.playedSec / 60) > 0 || s.events.some((e) => e.player_id === p.id));
      setState({
        phase: "share",
        match: s.match,
        cards: cards.map((p) => {
          const cnt = (t) => s.events.filter((e) => e.type === t && e.player_id === p.id).length;
          return {
            playerName: p.name, titular: p.titular, tg: cnt("amarilla"), tr: cnt("roja"), posicion: p.posicion || null,
            stats: { min: Math.round(p.playedSec / 60), gols: cnt("gol"), asist: cnt("asistencia"),
                     remates: cnt("remate") + cnt("remate_cabeza"), pases: cnt("pase_ok"),
                     regates: cnt("regate"), duelos: cnt("duelo"), recup: cnt("recuperacion"),
                     claves: cnt("pase_clave"), perdidas: cnt("perdida"), aereos: cnt("aereo"),
                     interc: cnt("intercepcion"), entradas: cnt("entrada"), centros: cnt("centro") },
          };
        }),
        result: { gf, gc },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar (tus datos siguen aquí)");
    } finally { setSaving(false); }
  };

  const abort = () => {
    if (!window.confirm("¿Descartar este partido en directo? Se perderá lo registrado.")) return;
    clearLive();
    navigate("/familia");
  };

  // ================= RENDER =================
  if (state.phase === "setup") return <SetupScreen players={players} onStart={startMatch} onCancel={() => navigate("/familia")} />;

  if (state.phase === "share") {
    return <ShareScreen match={state.match} result={state.result} cards={state.cards} playersInfo={players}
      onDone={() => { clearLive(); navigate("/familia"); }} />;
  }

  if (state.phase === "review") {
    return (
      <div className="max-w-md mx-auto space-y-4" data-testid="live-review">
        <h1 className="font-heading font-black uppercase text-2xl">Resumen del partido</h1>
        <div className="border border-border p-4 text-center">
          <div className="overline text-[10px]">{state.match.opponent} · {state.match.home_away === "casa" ? "En casa" : "Fuera"}</div>
          <div className="font-heading font-black text-4xl mt-1" data-testid="live-review-score">{summary.gf} - {summary.gc}</div>
        </div>
        {state.players.map((pl) => {
          const p = summary.per[pl.id];
          if (!p) return null;
          return (
            <div key={pl.id} className="border border-border p-3 text-sm" data-testid="live-review-player">
              <b className="font-heading uppercase">{p.name}</b> · {p.titular ? "Titular" : "Suplente"}
              <div className="text-xs text-muted-foreground mt-1">⏱️ {p.min}' · ⚽ {p.gols} · 🅰 {p.asist} · 🎯 {p.remates}{p.tg ? ` · 🟨${p.tg}` : ""}{p.tr ? ` · 🟥${p.tr}` : ""}</div>
              {(p.pases + p.perdidas + p.recup + p.regates + p.duelos + p.aereos + p.interc + p.entradas + p.centros + p.claves > 0) && (
                <div className="text-xs text-muted-foreground mt-0.5">✓ {p.pases} pases · 🔑 {p.claves} clave · ✕ {p.perdidas} pérd. · 🔄 {p.recup} recup. · 🏃 {p.regates} regates · ⚔ {p.duelos} duelos · 🆙 {p.aereos} aéreo · 🛡 {p.interc} interc. · 🦵 {p.entradas} entradas · ↪ {p.centros} centros</div>
              )}
              <div className="mt-3">
                <PositionPicker value={pl.posicion || null} testId={`live-pos-${pl.id}`}
                  onChange={(pos) => setState((s) => ({ ...s, players: s.players.map((x) => x.id === pl.id ? { ...x, posicion: pos } : x) }))} />
              </div>
            </div>
          );
        })}
        <p className="text-[10px] text-muted-foreground">Si algo no cuadra, vuelve al directo y corrige los eventos. Podrás borrar el partido después si hace falta.</p>
        <div className="flex gap-2">
          <button onClick={() => setState((s) => ({ ...s, phase: "live" }))} data-testid="live-back-btn" className="flex-1 h-12 border border-border hover:border-primary text-xs uppercase font-heading tracking-wider">← Volver al directo</button>
          <button onClick={save} disabled={saving} data-testid="live-save-btn" className="btn-volt flex-1 h-12">{saving ? "Guardando…" : "Guardar partido"}</button>
        </div>
      </div>
    );
  }

  // LIVE
  const evs = [...state.events].reverse();
  return (
    <div className="max-w-md mx-auto space-y-4 pb-8" data-testid="live-match">
      <div className="border-2 border-primary p-4 text-center space-y-2">
        <div className="overline text-[10px]">{state.match.opponent} · {state.match.home_away === "casa" ? "En casa" : "Fuera"} · <span data-testid="live-score">{summary.gf}-{summary.gc}</span> · <span data-testid="live-half" className={state.clock.half === "ht" ? "text-sky-400" : "text-primary"}>{state.clock.half === "ht" ? "Descanso" : state.clock.half === 2 ? "2ª parte" : "1ª parte"}</span></div>
        <div className="font-heading font-black text-5xl tabular-nums" data-testid="live-clock">{fmt(elapsed)}</div>
        <div className="flex justify-center gap-2 flex-wrap">
          {state.clock.half === "ht" ? (
            <button onClick={startSecondHalf} data-testid="live-second-half-btn" className="btn-volt h-11 px-6 inline-flex items-center gap-2 font-heading font-black uppercase text-xs tracking-wider">
              <Play size={14} /> Empezar 2ª parte
            </button>
          ) : (
            <>
              <button onClick={toggleClock} data-testid="live-clock-toggle" className={`h-11 px-5 inline-flex items-center gap-2 font-heading font-black uppercase text-xs tracking-wider ${state.clock.running ? "border-2 border-amber-400 text-amber-400" : "btn-volt"}`}>
                {state.clock.running ? <><Pause size={14} /> Pausa</> : <><Play size={14} /> {elapsed > 0 ? "Reanudar" : "Iniciar partido"}</>}
              </button>
              {(state.clock.half || 1) === 1 && elapsed > 0 && (
                <button onClick={halfTime} data-testid="live-halftime-btn" className="h-11 px-4 border-2 border-sky-400 text-sky-400 inline-flex items-center gap-1.5 font-heading font-black uppercase text-xs tracking-wider">
                  ⏸ Descanso
                </button>
              )}
            </>
          )}
          <button onClick={finish} data-testid="live-finish-btn" className="h-11 px-4 border-2 border-emerald-500 text-emerald-400 inline-flex items-center gap-1.5 font-heading font-black uppercase text-xs tracking-wider">
            <Flag size={13} /> Final
          </button>
        </div>
      </div>

      {state.players.map((p) => (
        <div key={p.id} className="border border-border p-3 space-y-2" data-testid={`live-player-${p.id}`}>
          <div className="flex items-center justify-between">
            <span className="font-heading font-black uppercase text-sm">{p.name.split(" ")[0]} <span className="text-muted-foreground font-normal text-xs">· {playerMinutes(p)}'</span></span>
            <button onClick={() => toggleField(p.id)} data-testid={`live-field-toggle-${p.id}`} className={`h-10 px-4 text-[11px] uppercase font-heading font-black tracking-wider border-2 ${p.onField ? "border-emerald-500 text-emerald-400" : "border-sky-400 text-sky-400"}`}>
              {p.onField ? "🔁 Sale al banquillo" : "▶ Vuelve a entrar"}
            </button>
            <span className="text-[10px] font-mono text-muted-foreground">⏱ {playerMinutes(p)}' {p.onField ? "· en el campo" : "· en el banquillo"}</span>
          </div>
          {p.onField && (
            <>
              <div className="grid grid-cols-3 gap-2">
                {BASIC_BTNS.map(([t, l]) => (
                  <button key={t} onClick={() => { addEvent(t, p.id); if (t === "gol") toast.success(`⚽ ¡GOL de ${p.name.split(" ")[0]}! min ${minuto}`); }} data-testid={`live-btn-${t}-${p.id}`} className="relative h-14 border border-border hover:border-primary active:bg-primary/20 font-heading font-bold text-sm transition-colors">
                    {l}
                    {cntFor(t, p.id) > 0 && <span className="absolute top-0.5 right-1 text-[10px] font-mono text-primary">{cntFor(t, p.id)}</span>}
                  </button>
                ))}
                <button onClick={() => { const txt = window.prompt("⭐ ¿Qué ha pasado? (ej. 'Gran jugada por banda')"); if (txt && txt.trim().length >= 3) { addEvent("momento", p.id, txt.trim()); toast.success("Momento guardado ⭐"); } }} data-testid={`live-btn-momento-${p.id}`} className="h-14 border border-amber-400/60 text-amber-300 hover:border-amber-400 font-heading font-bold text-sm transition-colors col-span-3">
                  ⭐ Momento destacado
                </button>
              </div>
              {state.adv ? (
                <div className="grid grid-cols-5 gap-1.5" data-testid={`live-tech-${p.id}`}>
                  {TECH_BTNS.map(([t, l]) => (
                    <button key={t} onClick={() => addEvent(t, p.id)} data-testid={`live-btn-${t}-${p.id}`} className="relative h-12 border border-border hover:border-primary active:bg-primary/20 font-heading font-bold text-[10px] leading-tight transition-colors">
                      {l}
                      {cntFor(t, p.id) > 0 && <span className="absolute top-0 right-0.5 text-[10px] font-mono text-primary">{cntFor(t, p.id)}</span>}
                    </button>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      ))}

      <button onClick={() => setState((s) => { try { localStorage.setItem("familia_adv_mode", s.adv ? "0" : "1"); } catch {} return { ...s, adv: !s.adv }; })} data-testid="live-adv-toggle" className="w-full h-9 border border-dashed border-border hover:border-primary text-[10px] uppercase font-heading tracking-wider text-muted-foreground hover:text-primary transition-colors">
        {state.adv ? "− Ocultar técnica (pases, regates, duelos…)" : "+ Modo técnico (pases, pérdidas, regates, duelos…)"}
      </button>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { addEvent("gol_equipo"); toast.success(`⚽ Gol del equipo · min ${minuto}`); }} data-testid="live-btn-gol-equipo" className="h-12 border border-border hover:border-primary font-heading font-bold text-sm">⚽ Gol del equipo</button>
        <button onClick={() => { addEvent("gol_rival"); toast(`🥅 Gol rival · min ${minuto}`); }} data-testid="live-btn-gol-rival" className="h-12 border border-border hover:border-red-500 font-heading font-bold text-sm">🥅 Gol rival</button>
      </div>

      {evs.length > 0 && (
        <div className="border border-border divide-y divide-border max-h-52 overflow-y-auto" data-testid="live-events">
          {evs.map((e) => {
            const pl = state.players.find((x) => x.id === e.player_id);
            return (
              <div key={e.id} className="p-2 flex items-center gap-2 text-xs">
                <span className="font-mono text-primary w-8 shrink-0">{e.min}'</span>
                <span className="flex-1 truncate">{EVENT_LABEL[e.type]}{pl ? ` · ${pl.name.split(" ")[0]}` : ""}{e.texto ? ` · ${e.texto}` : ""}</span>
                <button onClick={() => delEvent(e.id)} data-testid={`live-event-del-${e.id}`} className="w-6 h-6 border border-border hover:border-red-500 hover:text-red-400 flex items-center justify-center shrink-0"><Trash2 size={10} /></button>
              </div>
            );
          })}
        </div>
      )}

      <button onClick={abort} data-testid="live-abort-btn" className="w-full h-9 text-[10px] uppercase font-heading tracking-wider text-muted-foreground hover:text-red-400 inline-flex items-center justify-center gap-1"><X size={11} /> Descartar partido</button>
    </div>
  );
}

function ShareScreen({ match, result, cards, playersInfo, onDone }) {
  const [previews, setPreviews] = useState({});
  useEffect(() => {
    (async () => {
      const out = {};
      for (const c of cards) {
        const info = playersInfo.find((p) => p.name === c.playerName);
        const blob = await buildShareCard({
          playerName: c.playerName, dorsal: info?.jersey_number, titular: c.titular, tg: c.tg, tr: c.tr, posicion: c.posicion,
          result, opponent: match.opponent, homeAway: match.home_away, date: match.date, stats: c.stats,
        });
        out[c.playerName] = blob;
      }
      setPreviews(out);
    })();
  }, []); // eslint-disable-line
  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="live-share">
      <div className="text-center">
        <div className="text-4xl">🎉</div>
        <h1 className="font-heading font-black uppercase text-2xl mt-2">¡Partido guardado!</h1>
        <p className="text-xs text-muted-foreground mt-1">Comparte la tarjeta del partido por WhatsApp — a la familia le va a encantar.</p>
      </div>
      {cards.map((c) => {
        const blob = previews[c.playerName];
        return (
          <div key={c.playerName} className="border border-border p-3 space-y-3" data-testid={`share-card-${c.playerName}`}>
            {blob ? (
              <img src={URL.createObjectURL(blob)} alt={`Tarjeta de ${c.playerName}`} className="w-full border border-border" data-testid="share-card-preview" />
            ) : (
              <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">Generando tarjeta…</div>
            )}
            <button
              disabled={!blob}
              onClick={async () => {
                const r = await shareCard(blob, c.playerName);
                if (r === "downloaded") toast.success("Tarjeta descargada — adjúntala en WhatsApp 📲");
              }}
              data-testid="share-card-btn"
              className="btn-volt w-full h-12 inline-flex items-center justify-center gap-2"
            >
              <Share2 size={15} /> Compartir tarjeta
            </button>
          </div>
        );
      })}
      <button onClick={onDone} data-testid="share-done-btn" className="w-full h-10 border border-border hover:border-primary text-xs uppercase font-heading tracking-wider">Ir al panel →</button>
    </div>
  );
}

function SetupScreen({ players, onStart, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [match, setMatch] = useState({ date: today, opponent: "", home_away: "casa" });
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (players.length && !rows) setRows(players.map((p) => ({ id: p.id, name: p.name, playing: true, titular: true })));
  }, [players, rows]);
  const start = () => {
    if (!rows) return;
    if (!match.opponent.trim()) return toast.error("¿Contra quién juega?");
    if (!rows.some((r) => r.playing)) return toast.error("Marca al menos un jugador");
    onStart({ match: { ...match, opponent: match.opponent.trim() }, players: rows });
  };
  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="live-setup">
      <div>
        <div className="overline text-primary">🔴 Modo directo</div>
        <h1 className="font-heading font-black uppercase text-2xl mt-1">Partido en vivo</h1>
        <p className="text-xs text-muted-foreground mt-1">Apunta cada acción al momento — el minuto se registra solo. Si se te cierra el móvil, no pasa nada: seguirá donde lo dejaste.</p>
      </div>
      <input className="input-dark w-full" placeholder="Rival (ej. CD Los Olivos)" value={match.opponent} onChange={(e) => setMatch({ ...match, opponent: e.target.value })} data-testid="live-setup-opponent" />
      <div className="grid grid-cols-2 gap-2">
        <input type="date" className="input-dark" value={match.date} onChange={(e) => setMatch({ ...match, date: e.target.value })} data-testid="live-setup-date" />
        <select className="input-dark" value={match.home_away} onChange={(e) => setMatch({ ...match, home_away: e.target.value })} data-testid="live-setup-homeaway">
          <option value="casa">En casa</option><option value="fora">Fuera</option>
        </select>
      </div>
      <div className="space-y-2">
        {(rows || []).map((r, i) => (
          <div key={r.id} className="border border-border p-3 flex items-center justify-between gap-2 text-xs" data-testid={`live-setup-row-${i}`}>
            <label className="flex items-center gap-2 font-heading font-bold uppercase">
              <input type="checkbox" checked={r.playing} onChange={(e) => setRows(rows.map((x, ix) => ix === i ? { ...x, playing: e.target.checked } : x))} data-testid={`live-setup-playing-${i}`} />
              {r.name}
            </label>
            {r.playing && (
              <select className="input-dark h-8" value={r.titular ? "titular" : "suplente"} onChange={(e) => setRows(rows.map((x, ix) => ix === i ? { ...x, titular: e.target.value === "titular" } : x))} data-testid={`live-setup-titular-${i}`}>
                <option value="titular">Titular</option><option value="suplente">Suplente</option>
              </select>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={onCancel} data-testid="live-setup-cancel" className="h-12 px-4 border border-border hover:border-primary text-xs uppercase font-heading tracking-wider">Cancelar</button>
        <button onClick={start} disabled={!rows} data-testid="live-setup-start" className="btn-volt flex-1 h-12">{rows ? "🔴 Empezar directo" : "Cargando jugadores…"}</button>
      </div>
    </div>
  );
}
